import React, { Component, ReactNode } from 'react';

type Props = {
    children: (
        fields: Record<string, FormField>,
        state: {
            processing: boolean;
            success: ReactNode;
            valid: boolean;
            changed: boolean;
            submit: () => void;
            reset: () => void;
            setSuccess: (success: ReactNode) => void;
            setError: (name: string, override: ReactNode) => void;
        },
    ) => ReactNode;
    values?: Record<string, any> | null;
    validations?: Record<string, ((value: string, fields: Record<string, FormField>) => boolean) | string | boolean>;
    validationMessages?: Record<string, ((value: string, fields: Record<string, FormField>) => ReactNode) | ReactNode>;
    className?: string;
    successClassName?: string;
    action?: (
        fields: Record<string, FormField>,
        callbacks: {
            reset: () => void;
            setSuccess: (success: ReactNode) => void;
            setError: (name: string, override: ReactNode) => void;
        },
    ) => void | Promise<void>;
    testId?: string;
};

type State = {
    values: Record<string, string>;
    changes: Record<string, unknown>;
    validationErrorOverrides: Record<string, ReactNode>;
    processing: boolean;
    success: ReactNode;
};

export type FormField = {
    value: string;
    readonly changed: boolean;
    readonly valid: boolean;
    validationError: ReactNode;
    onChange: (value: string) => void;
};

export class Form extends Component<Props, State> {
    state: State = { values: {}, changes: {}, validationErrorOverrides: {}, processing: false, success: undefined };

    _fields: Record<string, FormField> = {};
    _isMounted: boolean = false;

    fields = new Proxy(this, {
        get(target, name): FormField {
            if (typeof name === 'symbol') {
                throw new Error(`Expected all field names to be strings, got symbol: ${name.toString()}`);
            }

            if (!(name in target._fields)) {
                const field = {
                    get value() {
                        const value = target.state.values[name];
                        if (value !== undefined) return value;
                        return target.props.values?.[name] ?? '';
                    },
                    set value(value) {
                        target.setState((state) => ({
                            values: { ...state.values, [name]: value },
                            changes: { ...state.changes, [name]: true },
                            validationErrorOverrides: { ...state.validationErrorOverrides, [name]: undefined },
                        }));
                    },
                    get changed() {
                        return !!target.state.changes[name];
                    },
                    get valid() {
                        const override = target.state.validationErrorOverrides[name];
                        if (override !== undefined) return !!override;
                        return target.validate(name);
                    },
                    get validationError() {
                        const override = target.state.validationErrorOverrides[name];
                        if (override !== undefined) return override;
                        if (!this.changed || this.valid) return undefined;
                        return target.validationMessage(name);
                    },
                    set validationError(override) {
                        target.setState((state) => ({
                            validationErrorOverrides: { ...state.validationErrorOverrides, [name]: override },
                        }));
                    },
                    onChange(value: string) {
                        target.setState((state) => ({
                            values: { ...state.values, [name]: value },
                            changes: { ...state.changes, [name]: true },
                            validationErrorOverrides: { ...state.validationErrorOverrides, [name]: undefined },
                        }));
                    },
                } satisfies FormField;

                target._fields[name] = field;
            }

            return target._fields[name]!;
        },
    }) as unknown as Record<string, FormField>;

    validate(name: string) {
        const validation = this.props.validations?.[name];
        const field = this.fields[name];

        if (!field) {
            return false;
        }

        const { value } = field;
        switch (typeof validation) {
            case 'function':
                return !!validation(value, this.fields);
            case 'string':
                return !!value.match(validation);
            default:
                return !validation || !!value;
        }
    }

    validationMessage(name: string) {
        const validationMessage = this.props.validationMessages?.[name];
        const field = this.fields[name];

        if (!field) {
            return;
        }

        const { value } = field;
        switch (typeof validationMessage) {
            case 'function':
                return validationMessage(value, this.fields);
            default:
                return validationMessage;
        }
    }

    validateAll() {
        return !Object.keys(this.props.validations ?? {}).find((name) => !this.fields[name]?.valid);
    }

    checkChanges() {
        for (const name in this.state.values) if (this.state.values[name] !== (this.props.values?.[name] ?? '')) return true;
        return false;
    }

    reset = () => {
        if (this._isMounted) this.setState({ values: {}, changes: {}, validationErrorOverrides: {}, success: undefined });
    };

    setError = (name: string, override: ReactNode) => {
        if (this._isMounted) this.fields[name]!.validationError = override;
    };

    setSuccess = (success: ReactNode) => {
        if (this._isMounted) this.setState({ success });
    };

    submit = async () => {
        try {
            const { action } = this.props;

            this.setState({ processing: true });
            await action?.(this.fields, {
                reset: this.reset,
                setError: this.setError,
                setSuccess: this.setSuccess,
            });
        } catch {
            // ignore
        } finally {
            if (this._isMounted) this.setState({ processing: false });
        }
    };

    onKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();

            if (this.validateAll() && !this.state.processing) this.submit();
        }
    };

    componentDidMount() {
        this._isMounted = true;
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    render() {
        const valid = this.validateAll();
        const changed = this.checkChanges();
        const { className, successClassName, testId } = this.props;
        const { processing, success } = this.state;

        return (
            <form data-testid={testId} className={className} onKeyDown={this.onKeyDown}>
                {success && <div className={successClassName}>{success}</div>}
                {this.props.children(this.fields, {
                    processing,
                    success,
                    valid,
                    changed,
                    submit: this.submit,
                    reset: this.reset,
                    setError: this.setError,
                    setSuccess: this.setSuccess,
                })}
            </form>
        );
    }
}
